
import { useEffect, useState } from 'react';
import { Button, Divider, Space, Table, Tag, Typography, Form, DatePicker, Row, Col, Select, message } from 'antd';
import { DownloadOutlined, } from '@ant-design/icons';
import { fetchModulePurchaseData, getModulePurchaseCsv } from './../../_services/reports.service';
import Cupoints from "./../../Components/Cupoints";
import moment from "moment";


const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterForm = ({ initialValue, hasData, onSearch, onExport, exporting }) => {
    const [form] = Form.useForm();

    const exportData = () => {
        const values = form.getFieldsValue();
        onExport(values);
    };

    const onFormValuesChange = (changedValues, allValues) => {
        onSearch(allValues)
    }

    return (
        <Form form={form} initialValues={initialValue} layout="inline" onValuesChange={onFormValuesChange} style={{ marginBottom: 24 }}>
            <Row style={{ width: '100%' }} justify="space-between" align="middle">
                {/* Left side: date inputs */}
                <Col>
                    <Space>
                        <Form.Item name="range">
                            <RangePicker
                                allowClear={false}
                                placeholder={['Start Date', 'End Date']}
                            />
                        </Form.Item>
                        <Form.Item name="courseType" label="Course Type">
                            <Select
                                placeholder="Select Course Type"
                                style={{ width: 100 }}
                                defaultValue=""
                            >
                                <Option value="">Any</Option>
                                <Option value="free">Free</Option>
                                <Option value="premium">Premium</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="permissionType" label="Permission">
                            <Select
                                placeholder="Select Permission"
                                style={{ width: 100 }}
                                defaultValue=""
                            >
                                <Option value="">Any</Option>
                                <Option value="permanent">Permanent</Option>
                                <Option value="temporary">Temporary</Option>
                            </Select>
                        </Form.Item>
                    </Space>
                </Col>

                {/* Right side: buttons */}
                <Col>
                    <Space>
                        <Form.Item>
                            <Button icon={<DownloadOutlined />} onClick={exportData} disabled={!hasData} loading={exporting}>
                                Export to CSV
                            </Button>
                        </Form.Item>
                    </Space>
                </Col>
            </Row>
        </Form>
    );
};

const ReportModulePurchases = () => {

    const [loading, setLoading] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState();
    const [data, setData] = useState([]);
    const [formValue, setFormValue] = useState({
        range: [moment().startOf("month"), moment().endOf("month")],
        courseType: '',
        permissionType: '',
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
    });

    const columns = [
        {
            title: 'Course Name',
            dataIndex: 'collectionName',
            render: (_, { collection }) => (
                <>
                    {collection && (
                        <Space direction='vertical' size={0}>
                            <span>{collection.name} ({collection.id})</span>
                        </Space>
                    )}
                </>
            )
        }, {
            title: 'Type',
            dataIndex: 'freeCourse',
            render: (_, { collection }) => (
                <>
                    {collection && (
                        <Tag color={collection.freeCourse ? "blue" : "orange"}>{collection.freeCourse ? "Free" : "Premium"}</Tag>
                    )}
                </>
            )
        },
        {
            title: 'Module Name',
            dataIndex: 'module',
            render: (_, { module }) => (
                <>
                    {module.name} ({module.id})
                </>
            )
        },
        {
            title: 'User Name',
            dataIndex: 'user',
            render: (_, { user }) => (
                <>
                    <Space direction='vertical' size={0}>
                        <span>
                            {user.firstName} {user.lastName} ({user.id})
                        </span>
                        <Text type="secondary" >
                            {user.username}
                        </Text>
                    </Space>
                </>
            )
        }, {
            title: 'Price',
            dataIndex: 'module',
            render: (_, { module }) => (
                <>
                    <Cupoints points={module.price}></Cupoints>
                </>
            )
        }, {
            title: 'Permission',
            dataIndex: 'accessType',
            render: (_, { accessType, accessUntil }) => (
                <>
                    <Tag color={accessType === "temporary" ? "red" : "green"}>{accessType}</Tag>
                    {accessUntil && (
                        <Text type="secondary" >
                            <br />
                            Until: {moment(accessUntil).format("L, LT")}
                        </Text>
                    )}
                </>
            )
        },
        {
            title: 'Purchase Date',
            dataIndex: 'accessFrom',
            render: (_, { accessFrom }) => (
                <>
                    {moment(accessFrom).format("L")}
                </>
            )
        },
    ];

    const fetchData = ({ current, pageSize }) => {
        setLoading(true);
        setError(null);

        fetchModulePurchaseData(formValue, {
            page: current,
            pageSize,
        }).then(response => {
            setData(response.data);
            setHasData(response.data.length > 0)
            setLoading(false);
            setPagination((prev) => ({
                ...prev,
                current,
                pageSize,
                total: response.total,
            }));
        }).catch(error => {
            setError("Something went wrong when fetch data!");
            setHasData(false);
            setLoading(false);
        })
    }

    const handleTableChange = (newPagination) => {
        fetchData({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    };

    const exportToCsv = (data) => {
        setExporting(true);
        getModulePurchaseCsv(data).then(() => {
            message.success("Report Downloaded!");
            setExporting(false);
        }).catch(error => {
            message.error("Something went wrong when export data!");
            setExporting(false);
        })
    }

    useEffect(() => {
        fetchData({
            current: pagination.current,
            pageSize: pagination.pageSize,
        });
    }, [formValue]);

    return <div>
        <h2>
            Module Purchases Report
        </h2>
        <Divider />
        <FilterForm hasData={hasData} initialValue={formValue}
            exporting={exporting}
            onSearch={(formData) => { setFormValue(formData) }}
            onExport={(formData) => { exportToCsv(formData) }} />

        <Table
            columns={columns}
            size="middle"
            rowKey={record => record.id}
            dataSource={data}
            locale={{
                emptyText: error || 'No Data found'
            }}
            loading={loading}
            pagination={{
                ...pagination,
                showTotal: (total) => `Total ${total} records`,
            }}
            onChange={handleTableChange}
        />
    </div>
}

export default ReportModulePurchases;